import { useEffect, useMemo, useRef, useState } from 'react';
import { getTemplateNames } from '../../data/templateAdmin';
import type { AuditRecord } from '../../data/audits';
import type { Store } from '../../data/types';
import { StoreSearchSelect } from '../StoreSearchSelect';
import { CompletedAuditRow } from './CompletedAuditRow';
import { isoToCompanyDay } from '../dates';
import { PagerBar, PagerTop, useFitPageSize, usePager } from '../Pager';

export interface CompletedAuditItem {
  audit: AuditRecord;
  store: Store;
}

type SortBy = 'date' | 'store' | 'score';

export interface CompletedAuditFilters {
  /** Store id; '' = every store. */
  storeId: string;
  /** Trimmed lower-case district; '' = every district. */
  district: string;
  /** The form's name (shared by every version of a form); '' = every audit type. */
  type: string;
  /** Eastern days (YYYY-MM-DD) the submit day must fall within; '' = open. */
  from: string;
  to: string;
  sortBy: SortBy;
}

/** The list's filters and sort (pure — tested). */
export function filterCompletedAudits(items: readonly CompletedAuditItem[], f: CompletedAuditFilters, formNames: ReadonlyMap<number, string>): CompletedAuditItem[] {
  const rows = items.filter(({ audit, store }) => {
    if (f.storeId && String(store.id) !== f.storeId) return false;
    if (f.district && store.district.trim().toLowerCase() !== f.district) return false;
    if (f.type && formNames.get(audit.templateId) !== f.type) return false;
    const day = isoToCompanyDay(audit.submittedAt);
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    return true;
  });
  return [...rows].sort((a, b) => {
    if (f.sortBy === 'store') {
      return a.store.storeNumber.localeCompare(b.store.storeNumber, undefined, { numeric: true }) || b.audit.submittedAt.localeCompare(a.audit.submittedAt);
    }
    if (f.sortBy === 'score') {
      // Lowest score first so problem audits surface; null (all-N/A) last.
      const as = a.audit.score ?? Number.POSITIVE_INFINITY;
      const bs = b.audit.score ?? Number.POSITIVE_INFINITY;
      return as - bs || b.audit.submittedAt.localeCompare(a.audit.submittedAt);
    }
    return b.audit.submittedAt.localeCompare(a.audit.submittedAt);
  });
}

/**
 * Completed-audits list with the standard volume controls: district and
 * audit-type filters (owner ask 2026-10-29: "the Audits screen is hard to
 * narrow down"), store filter (district optgroups; narrowed to the picked
 * district), sort and a submitted-date range, a page at a time. (Group by
 * store is gone — it unpaged the list into an endless scroll; the filters
 * narrow it instead, owner 2026-10-29.)
 * The district filter shows when the stores span more than one district
 * (never on a district page), the audit-type filter when the list holds
 * more than one form, the store controls when it covers more than one
 * store. Shared by the Audits hub, district pages and the store team's Home.
 */
export function CompletedAuditsList({
  items,
  filterStores,
  cap,
  context,
}: {
  /** Already scoped to the viewer; null = loading. */
  items: CompletedAuditItem[] | null;
  /** Stores offered in the filter dropdown (pre-sorted). */
  filterStores: Store[];
  /** The most rows a page holds (default 10); fewer when they don't fit 80% of the window, 3 on a phone. */
  cap?: number;
  /** 'district' on a district page: cards don't repeat the district. */
  context?: 'district';
}) {
  const [storeFilter, setStoreFilter] = useState('');
  /** Trimmed lower-case district; '' = every district. */
  const [districtFilter, setDistrictFilter] = useState('');
  /** A form's (newest) name — every version of a form shares it (two forms with the same name would share one option — names are unique in practice); '' = every audit type. */
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Each card names its form (UI review U32/U74); a failed read just leaves the names off.
  const [formNames, setFormNames] = useState<ReadonlyMap<number, string>>(new Map());
  useEffect(() => {
    let live = true;
    getTemplateNames()
      .then((names) => live && setFormNames(names))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const multiStore = filterStores.length > 1;
  const districtKey = (st: Store) => st.district.trim().toLowerCase();
  const districts = useMemo(() => {
    const seen = new Map<string, string>();
    for (const st of filterStores) {
      const name = st.district.trim();
      if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [filterStores]);
  const showDistricts = context !== 'district' && districts.length > 1;
  // The store picker offers only the picked district's stores.
  const storesShown = useMemo(
    () => (districtFilter ? filterStores.filter((st) => districtKey(st) === districtFilter) : filterStores),
    [filterStores, districtFilter],
  );
  const types = useMemo(() => {
    const names = new Set<string>();
    for (const { audit } of items ?? []) {
      const name = formNames.get(audit.templateId);
      if (name) names.add(name);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [items, formNames]);
  const filtersActive = storeFilter !== '' || districtFilter !== '' || typeFilter !== '' || fromDate !== '' || toDate !== '';

  const filtered = useMemo(
    () => (items === null ? null : filterCompletedAudits(items, { storeId: storeFilter, district: districtFilter, type: typeFilter, from: fromDate, to: toDate, sortBy }, formNames)),
    [items, storeFilter, districtFilter, typeFilter, formNames, fromDate, toDate, sortBy],
  );

  // A page at a time, filtered or not (owner ruling 2026-10-29: no section
  // over ~80% of the screen; older audits are a "Next" away, not a filter away).
  // UI review 3 (D3): 20 cards a page was 169–217% of the window — the drawn cards are measured and the
  // page fits 80% of it (at most `cap`; heading, note, filters and bar = chromePx).
  const listRef = useRef<HTMLDivElement>(null);
  const [firstGuess] = useState(() => Math.min(cap ?? 10, 10));
  const pageSize = useFitPageSize(listRef, { initial: firstGuess, chromePx: 200, phone: 2, max: Math.min(cap ?? 10, 10) });
  const pager = usePager(filtered, `${storeFilter}|${districtFilter}|${typeFilter}|${fromDate}|${toDate}|${sortBy}`, pageSize);

  if (items !== null && items.length === 0) {
    return <p className="muted">No submitted audits yet.</p>;
  }

  return (
    <>
      {items !== null && (
        <div className="list-tools">
          {showDistricts && (
            <select
              value={districtFilter}
              onChange={(e) => {
                const next = e.target.value;
                setDistrictFilter(next);
                // A store from another district would leave the list empty.
                if (next && storeFilter && !filterStores.some((st) => String(st.id) === storeFilter && districtKey(st) === next)) setStoreFilter('');
              }}
              aria-label="District"
            >
              <option value="">All districts</option>
              {districts.map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {types.length > 1 && (
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Audit type">
              <option value="">All audit types</option>
              {types.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {multiStore && (
            <>
              <StoreSearchSelect
                stores={storesShown}
                value={storeFilter}
                onChange={setStoreFilter}
                placeholder="All stores"
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                aria-label="Sort"
              >
                <option value="date">Sort: newest first</option>
                <option value="store">Sort: store</option>
                <option value="score">Sort: lowest score</option>
              </select>
            </>
          )}
          <label className="date-range">
            Submitted from
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              aria-label="Submitted on or after"
            />
          </label>
          <label className="date-range">
            to
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              aria-label="Submitted on or before"
            />
          </label>
          {filtersActive && (
            <button
              className="btn btn-small"
              onClick={() => {
                setStoreFilter('');
                setDistrictFilter('');
                setTypeFilter('');
                setFromDate('');
                setToDate('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {filtered === null && <p>Loading…</p>}
      {filtered !== null && filtered.length === 0 && (
        <p className="muted">No audits match these filters.</p>
      )}

      <PagerTop pager={pager} />
      <div ref={listRef} className="pager-list">
        {pager.items.map(({ audit, store }) => (
          <CompletedAuditRow key={audit.id} audit={audit} store={store} context={context} formName={formNames.get(audit.templateId)} />
        ))}
      </div>
      <PagerBar pager={pager} noun="audits" />
    </>
  );
}
